import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft,
  Calendar,
  Users,
  TrendingUp,
  Tag,
  BarChart2,
  Edit,
  Power,
  PowerOff,
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';

const COLORS = {
  orange: '#E87E04',
  green: '#008751',
};

const BackendUrl = process.env.REACT_APP_Backend_Url;

export default function PromoCodeDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();

  const [promoCode, setPromoCode] = useState(null);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const admin = JSON.parse(localStorage.getItem('AdminEcomme'));
    if (admin) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${admin.token}`;
    }

    fetchData();
  }, [id]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [promoRes, statsRes] = await Promise.all([
        axios.get(`${BackendUrl}/api/promocodes/admin/${id}`, {
          withCredentials: true,
        }),
        axios.get(`${BackendUrl}/api/promocodes/admin/${id}/stats`, {
          withCredentials: true,
        }),
      ]);

      setPromoCode(promoRes.data.promoCode);
      setStats(statsRes.data.stats);
    } catch (error) {
      console.error('Erreur:', error);
      toast({
        title: 'Erreur de chargement',
        description: error.response?.data?.message || 'Impossible de charger les détails du code promo. Le code n\'existe peut-être plus.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    try {
      await axios.patch(
        `${BackendUrl}/api/promocodes/admin/${id}/toggle`,
        {},
        { withCredentials: true }
      );

      toast({
        title: 'Succès',
        description: `Code promo ${promoCode.isActive ? 'désactivé' : 'activé'}`,
      });

      fetchData();
    } catch (error) {
      console.error('Erreur:', error);
      toast({
        title: 'Erreur de modification',
        description: error.response?.data?.message || 'Impossible de modifier le statut du code promo.',
        variant: 'destructive',
      });
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatValue = (promo) => {
    if (promo.type === 'percentage') {
      return `${promo.value}%`;
    }
    return `${promo.value.toLocaleString('fr-FR')} €`;
  };

  const getStatusBadge = (promo) => {
    const now = new Date();
    const endDate = new Date(promo.endDate);

    if (!promo.isActive) {
      return <Badge variant="secondary">Inactif</Badge>;
    }

    if (endDate < now) {
      return <Badge variant="destructive">Expiré</Badge>;
    }

    if (promo.maxUsage && promo.currentUsage >= promo.maxUsage) {
      return <Badge variant="destructive">Épuisé</Badge>;
    }

    return <Badge style={{ backgroundColor: COLORS.green }}>Actif</Badge>;
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <Skeleton className="h-10 w-64 mb-6" />
        <div className="grid gap-4 md:grid-cols-4 mb-6">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-96 mb-6" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!promoCode) {
    return (
      <div className="p-6">
        <p className="text-center text-gray-500">Code promo non trouvé</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <Button
            variant="ghost"
            onClick={() => navigate('/Admin/PromoCodes')}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>
          <div className="flex items-center gap-4">
            <h1 className="text-3xl font-bold">{promoCode.code}</h1>
            {getStatusBadge(promoCode)}
          </div>
          {promoCode.description && (
            <p className="text-gray-600 mt-2">{promoCode.description}</p>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleToggleStatus}
          >
            {promoCode.isActive ? (
              <>
                <PowerOff className="mr-2 h-4 w-4" />
                Désactiver
              </>
            ) : (
              <>
                <Power className="mr-2 h-4 w-4" />
                Activer
              </>
            )}
          </Button>
          <Link to={`/Admin/EditPromoCode/${id}`}>
            <Button style={{ backgroundColor: COLORS.orange }}>
              <Edit className="mr-2 h-4 w-4" />
              Modifier
            </Button>
          </Link>
        </div>
      </div>

      {/* Statistiques */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Utilisations
              </CardTitle>
              <Tag className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalUsage}</div>
              <p className="text-xs text-muted-foreground">
                {stats.remainingUsage !== 'Illimité'
                  ? `${stats.remainingUsage} restantes`
                  : 'Illimité'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Utilisateurs uniques
              </CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.uniqueUsers}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Réduction totale
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats.totalDiscountGiven?.toLocaleString('fr-FR')} €
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Moyenne par utilisation
              </CardTitle>
              <BarChart2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {Math.round(stats.averageDiscountPerUse)?.toLocaleString('fr-FR')} €
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Informations du code */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Type:</span>
              <span className="font-medium">
                {promoCode.type === 'percentage'
                  ? 'Pourcentage'
                  : 'Montant fixe'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Valeur:</span>
              <span className="font-medium">{formatValue(promoCode)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Montant minimum:</span>
              <span className="font-medium">
                {promoCode.minOrderAmount.toLocaleString('fr-FR')} €
              </span>
            </div>
            {promoCode.maxDiscount && (
              <div className="flex justify-between">
                <span className="text-gray-600">Réduction maximum:</span>
                <span className="font-medium">
                  {promoCode.maxDiscount.toLocaleString('fr-FR')} €
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Limites et validité</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Début:</span>
              <span className="font-medium">
                {new Date(promoCode.startDate).toLocaleDateString('fr-FR')}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Fin:</span>
              <span className="font-medium">
                {new Date(promoCode.endDate).toLocaleDateString('fr-FR')}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Max utilisations:</span>
              <span className="font-medium">
                {promoCode.maxUsage || 'Illimité'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Max par utilisateur:</span>
              <span className="font-medium">{promoCode.maxUsagePerUser}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Historique d'utilisation */}
      <Card>
        <CardHeader>
          <CardTitle>Historique d'utilisation</CardTitle>
        </CardHeader>
        <CardContent>
          {promoCode.usageHistory && promoCode.usageHistory.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Utilisateur</TableHead>
                  <TableHead>Commande</TableHead>
                  <TableHead className="text-right">Réduction</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {promoCode.usageHistory
                  .slice()
                  .reverse()
                  .map((usage, index) => (
                    <TableRow key={index}>
                      <TableCell>{formatDate(usage.usedAt)}</TableCell>
                      <TableCell>
                        {usage.userId?.name || usage.userId?.email || 'N/A'}
                      </TableCell>
                      <TableCell>
                        {usage.orderId?.reference || usage.orderId?._id || 'N/A'}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {usage.discountAmount.toLocaleString('fr-FR')} €
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-center text-gray-500 py-8">
              Aucune utilisation pour l'instant
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
